import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import update from 'immutability-helper';
import DatePicker from 'react-datepicker';
import SVG from 'react-inlinesvg';
import 'react-datepicker/dist/react-datepicker.css';
import { postRequest, putRequest, getRequest } from '../../requestUtils';
import { isWeekday } from '../../dateUtils';
import { isMobile } from '../../screenUtils';
import { analyticsTrack } from '../../analyticsUtils';
import TimezoneSelector from '../common/TimezoneSelector';
import {
  minStartTime, minEndTime,
  maxStartTime, maxEndTime,
  completedAvailabilities,
  overlappingAvailabilities,
  startTimeInPastValidator,
  formatToRubyStyle,
  formatToJavaScriptStyle,
} from '../../scheduleInterviewUtils';
import SelectOrInviteNewCoworkers from '../common/SelectOrInviteNewCoworkers';
import checkMarkIcon from '../../../assets/images/check-mark.svg';
import arrowLeftIcon from '../../../assets/images/icons/icon-left-arrow-gray.svg';
import arrowRightIcon from '../../../assets/images/icons/icon-right-arrow.svg';
import phoneIcon from '../../../assets/images/phone-square-green.svg';
import Loading from '../common/Loading';
import './ScheduleInterviewModal.scss';

const NUM_SLOTS_REQUIRED = 2;

const utmParams = {
  source: 'introfeedback',
  medium: 'inapp',
  campaign: 'hiringmanagerintro',
};

const npsUrl = `${window.location.pathname}?prompt_nps=true&`
      + `utm_source=${utmParams.source}&`
      + `utm_medium=${utmParams.medium}&`
      + `utm_campaign=${utmParams.campaign}`;

// a being the availability object/struct with date, start_time, and end_time fields.
// (All of which are Date objects)
const keyFrom = (a, index) => {
  const date = a.date && a.date.getTime();
  const startTime = a.start_time && a.start_time.getTime();
  const endTime = a.end_time && a.end_time.getTime();

  return `${date}${startTime}${endTime}${index}`;
};

// filters any incomplete availablities and converts date objects to strings.
const availabilitiesDateToString = (availabilities) => {
  const completeAvailabilities = completedAvailabilities(availabilities);

  return completeAvailabilities.map((availability) => (
    {
      date: moment(availability.date).format('YYYY-MM-DD'),
      start_time: moment(availability.start_time).format('HH:mm'),
      end_time: moment(availability.end_time).format('HH:mm'),
    }
  ));
};

const availabilitiesStringToDate = (availabilities) => {
  // availabilities are passed in as strings and to pass them to ScheduleInterviewModal they need
  // to be JS Date objects.

  /* Note: start_time and end_time take today's date because the timepickers need to be set
    to all have the same date for the overlap checks to work (that's how they work by default when
    creating an interview, and so to edit an interview we are trying to recreate that)
    They do not take the date of the availability as one might expect.
  */
  const date = new Date();
  const availabilitiesAsDates = availabilities.map(
    (avail) => (
      avail.date && avail.start_time && avail.end_time
        ? {
          date: moment(avail.date).toDate(),
          start_time: moment(`${date.toDateString()} ${avail.start_time}`).toDate(),
          end_time: moment(`${date.toDateString()} ${avail.end_time}`).toDate(),
        }
        : {
          date: null,
          start_time: null,
          end_time: null,
        }
    ),
  );

  return availabilitiesAsDates;
};

// used by default propTypes
const defaultAvailabilities = () => {
  const defaultSlots = [];

  for (let i = 0; i < NUM_SLOTS_REQUIRED + 1; i += 1) {
    defaultSlots.push({
      date: null,
      start_time: null,
      end_time: null,
    });
  }

  return defaultSlots;
};

const interviewParams = (props, state, type = 'creating', sendNotifications = null) => {
  const {
    proposalResourceId,
    reasons,
    internalNotes,
    interviewReschedule,
    newInterviewSlot,
  } = props;

  const {
    availabilities,
    timezone,
    selectedCoworkers,
    newCoworkers,
    selectedTimeSlots,
    selectedInvitationRole,
  } = state;

  const selectedCoworkersEmails = selectedCoworkers.map((coworker) => coworker.value);

  const baseParams = {
    interview: {
      timezone,
      availabilities: newInterviewSlot
        ? formatToRubyStyle(selectedTimeSlots)
        : availabilitiesDateToString(availabilities),
      invited_emails: [...selectedCoworkersEmails, ...newCoworkers],
    },
    project_permissions: {
      emails: newCoworkers,
      level: selectedInvitationRole,
    },
  };

  const typeParams = {
    creating: {
      proposal_resource_id: proposalResourceId,
      reason: internalNotes,
      internal_feedback_ids: reasons,
    },
    updating: {
      reschedule: interviewReschedule,
      send_notifications: sendNotifications,
    },
  };

  return {
    ...baseParams,
    ...typeParams[type],
  };
};

const formatTimeSlot = (date, time) => ({ date, startTime: time });

const hasMatchingTimeSlot = (timeSlots, targetSlot) => timeSlots.some(({ date, startTime }) => date === targetSlot.date && startTime === targetSlot.startTime);

class ScheduleInterviewModal extends Component {
  constructor(props) {
    super(props);
    const {
      isEditing, availabilities, selectedCoworkers, showModal,
    } = props;
    const initialAvailabilities = isEditing
      ? availabilitiesStringToDate(availabilities) : defaultAvailabilities();

    this.state = {
      availabilities: initialAvailabilities,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      selectedCoworkers,
      newCoworkers: [], // !! array of strings, not objects !!
      selectedInvitationRole: 'manager', // default project permissions level is manager
      overlapping: [],
      startTimeInPast: false,
      showModal,
      showSendNotificationModal: false,
      currentViewIndex: 1,
      selectedTimeSlots: isEditing ? formatToJavaScriptStyle(availabilities) : [],
      isFetchingSlots: false,
      availableInterviewTimeSlots: [],
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleTimezoneChange = this.handleTimezoneChange.bind(this);
    this.handleSelectTimeSlot = this.handleSelectTimeSlot.bind(this);
    this.handleAvailabilityChange = this.handleAvailabilityChange.bind(this);
    this.handleNewCoworkersChange = this.handleNewCoworkersChange.bind(this);
    this.getAvailableInterviewSlot = this.getAvailableInterviewSlot.bind(this);
    this.handleAddAvailabilitySlot = this.handleAddAvailabilitySlot.bind(this);
    this.renderNewAvailabilitiesTable = this.renderNewAvailabilitiesTable.bind(this);
    this.handleAvailabilityNavigation = this.handleAvailabilityNavigation.bind(this);
    this.handleSelectedCoworkersChange = this.handleSelectedCoworkersChange.bind(this);
    this.handleSelectedInvitationRoleChange = this.handleSelectedInvitationRoleChange.bind(this);
  }

  componentDidMount() {
    const { newInterviewSlot } = this.props;

    if (newInterviewSlot) {
      this.getAvailableInterviewSlot(
        Intl.DateTimeFormat().resolvedOptions().timeZone,
      );
    }
  }

  setShowModal(showModal) {
    const { handleInterviewModalClose, location } = this.props;

    if (showModal) {
      analyticsTrack('Opened Schedule Interview Modal', { location });
    } else {
      handleInterviewModalClose();
    }

    this.setState({ showModal });
  }

  async getAvailableInterviewSlot(timeZone) {
    const { availableInterviewSlotUrl, csrfToken } = this.props;
    const { timezone } = this.state;

    const selectedTimeZone = timezone || timeZone;

    this.setState({ isFetchingSlots: true });

    try {
      const { data } = await getRequest(availableInterviewSlotUrl, { timezone: selectedTimeZone }, csrfToken);

      this.setState({
        availableInterviewTimeSlots: data,
      });
    } catch (error) { // eslint-disable  no-empty
    } finally {
      this.setState({ isFetchingSlots: false });
    }
  }

  handleShowNotificationModal() {
    this.setState({ showModal: false, showSendNotificationModal: true });
  }

  ableToSubmit() {
    const {
      availabilities,
      timezone,
      overlapping,
      startTimeInPast,
      selectedTimeSlots,
    } = this.state;
    const { newInterviewSlot } = this.props;

    const completeAvailabilities = completedAvailabilities(availabilities);
    const hasRequiredSlots = completeAvailabilities.length >= NUM_SLOTS_REQUIRED;
    const hasValidTimezone = Boolean(timezone);
    const hasEnoughSelectedSlots = selectedTimeSlots.length >= NUM_SLOTS_REQUIRED;
    const noOverlap = overlapping.length === 0;
    const startTimeValid = !startTimeInPast;

    return newInterviewSlot
      ? (hasValidTimezone && hasEnoughSelectedSlots)
      : (hasValidTimezone && startTimeValid && noOverlap && hasRequiredSlots);
  }

  isModalOpen() {
    const { showModal, showSendNotificationModal } = this.state;
    return showModal || showSendNotificationModal;
  }

  handleTimezoneChange(newTimezone) {
    this.setState({ timezone: newTimezone }, () => {
      this.getAvailableInterviewSlot();
    });
  }

  // newAvailabilityField is technically a Date object for all three availability inputs
  // because we use the DatePicker component to select the interview date (obviously)
  // but also to select the start and end times of the availability window.
  //
  // For start_time and end_time fields, we only care about the time portion
  // but we must store it as a Date so it can be treated as such by the
  // DatePicker.
  //
  // Likewise, for the date field, we only care about the date portion, not the time.
  handleAvailabilityChange(newAvailabilityField, index, fieldToSet) {
    if (fieldToSet !== 'start_time' && fieldToSet !== 'end_time' && fieldToSet !== 'date') {
      return;
    }

    const { availabilities } = this.state;

    const newAvailabilities = update(availabilities, {
      [index]: { [fieldToSet]: { $set: newAvailabilityField } },
    });

    this.setState({
      availabilities: newAvailabilities,
      overlapping: overlappingAvailabilities(newAvailabilities),
      startTimeInPast: startTimeInPastValidator(newAvailabilities),
    });
  }

  handleAddAvailabilitySlot() {
    const { availabilities } = this.state;
    const newAvailabilities = update(availabilities, {
      $push: [{
        date: null, start_time: null, end_time: null,
      }],
    });

    this.setState({ availabilities: newAvailabilities });
  }

  handleSelectedCoworkersChange(newSelection) {
    this.setState({ selectedCoworkers: newSelection });
  }

  handleNewCoworkersChange(newCoworkers) {
    this.setState({ newCoworkers });
  }

  handleSelectedInvitationRoleChange(newRole) {
    this.setState({ selectedInvitationRole: newRole });
  }

  handleSelectTimeSlot(val) {
    this.setState((prevState) => {
      const { selectedTimeSlots } = prevState;

      const exists = hasMatchingTimeSlot(selectedTimeSlots, val);

      const newSelectedTimeSlots = exists
        ? selectedTimeSlots.filter(
          (slot) => !(slot.date === val.date && slot.startTime === val.startTime),
        )
        : [...selectedTimeSlots, val];

      return { selectedTimeSlots: newSelectedTimeSlots };
    });
  }

  handleSubmit() {
    const {
      createInterviewUrl,
      csrfToken,
      showNPSOnSubmit,
      location,
    } = this.props;

    const newUrl = showNPSOnSubmit ? npsUrl : window.location.pathname;
    const params = interviewParams(this.props, this.state);

    postRequest(
      createInterviewUrl,
      params,
      csrfToken,
    ).then(() => {
      analyticsTrack('Submitted Interview Request', { location });
      window.location.href = newUrl;
    }).catch(() => null);
  }

  handleAvailabilityNavigation(direction) {
    const { currentViewIndex, availableInterviewTimeSlots } = this.state;
    const increment = direction === 'back' ? -1 : 1;
    const newView = currentViewIndex + increment;

    if (newView >= 0 && newView < availableInterviewTimeSlots.length) {
      this.setState({ currentViewIndex: newView });
    }
  }

  handleUpdateSubmission(sendNotifications) {
    if (!this.ableToSubmit()) {
      return;
    }

    const {
      createInterviewUrl,
      csrfToken, location,
      interviewReschedule,
      interviewType,
    } = this.props;
    const params = interviewParams(this.props, this.state, 'updating', sendNotifications);
    const isReschedule = interviewReschedule || interviewType === 'reschedule';

    putRequest( // Because this is an update action, we PUT to createInterviewUrl rather than POST
      createInterviewUrl,
      params,
      csrfToken,
    ).then(() => {
      analyticsTrack(`${isReschedule ? 'Reschedule' : 'Edited'} Interview Request`, { location });
      window.location.reload();
    }).catch(() => null);
  }

  renderModalHeading() {
    const {
      isEditing, consultantName, interviewReschedule, interviewType,
    } = this.props;

    const action = !isEditing
      ? 'Add'
      : interviewReschedule || interviewType === 'reschedule'
        ? 'Reschedule'
        : 'Edit';

    return (
      <Fragment>
        <h2 className="mb-3">
          {action}
          {' '}
          your availability to interview
          {' '}
          {consultantName}
        </h2>
        <p className="body-text-2">
          Select at least
          {' '}
          {NUM_SLOTS_REQUIRED}
          {' '}
          days and times starting tomorrow when
          interviewers are available. The candidate will select from these times to
          book a 30 minute interview. Add times when you and participating
          coworker(s) are available.
        </p>
      </Fragment>
    );
  }

  renderTimezoneSelect() {
    const { timezone } = this.state;
    const { newInterviewSlot } = this.props;

    return (
      <div className="timezone-select mt-4">
        <div className="timezone-select-label body-text-2 medium-weight required mb-2">
          { newInterviewSlot ? 'Your current timezone' : 'Select timezone:' }
        </div>
        <TimezoneSelector
          onChange={(newTz) => this.handleTimezoneChange(newTz)}
          selectedValue={timezone}
        />
      </div>
    );
  }

  renderErrors() {
    const { overlapping, startTimeInPast } = this.state;

    if (overlapping.length === 0 && !startTimeInPast) { // no errors to display
      return null;
    }

    return (
      <div className="body-text-2 error-message mb-3">
        { overlapping.length > 0
          && (
          <div>
            <span>
              <i className="fas fa-exclamation-triangle me-2" />
            </span>
            Make sure there are no overlaps in your availability windows before continuing!
          </div>
          )}
        { startTimeInPast
          && (
          <div>
            <span>
              <i className="fas fa-exclamation-triangle me-2" />
            </span>
            Starting time cannot be earlier than current time!
          </div>
          )}
      </div>
    );
  }

  renderAvailabilityRow(availability, index) {
    const { proposalResourceId } = this.props;
    const isRequired = index < NUM_SLOTS_REQUIRED;

    let dayText = '';
    let fromText = '';
    let toText = '';

    if (isMobile()) {
      dayText = 'Day';
      fromText = 'From';
      toText = 'To';
    }

    return (
      <Fragment>
        <td className="availability-entry">
          <p className={`${isRequired && 'required'} medium-weight body-text-2`}>
            Slot
            {index + 1}
          </p>
        </td>
        <td className="availability-entry">
          <div className="availability-datepicker-container g-row">
            <label className="g-row--always" htmlFor={`calendar-${index}-${proposalResourceId}`}>
              <DatePicker
                id={`calendar-${index}-${proposalResourceId}`}
                selected={availability.date}
                onChange={(newDate) => this.handleAvailabilityChange(newDate, index, 'date')}
                dateFormat="MM/dd/yyyy"
                className="availability-datepicker"
                minDate={new Date()}
                filterDate={isWeekday} // only want to make weekdays available for selection
                placeholderText={dayText}
              />
              <span className="input-group-addon">
                <i className="fas fa-calendar adjust-center" />
              </span>
            </label>
          </div>
        </td>
        <td className="availability-entry">
          <div className="start-time-input-container g-row">
            <label className="g-row--always" htmlFor={`start-time-${index}-${proposalResourceId}`}>
              <DatePicker
                id={`start-time-${index}-${proposalResourceId}`}
                selected={availability.start_time}
                onChange={(newStartTime) => this.handleAvailabilityChange(newStartTime, index, 'start_time')}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={30}
                timeCaption="Time"
                dateFormat="h:mm aa"
                minTime={minStartTime(availability.date)}
                maxTime={maxStartTime(availability.end_time)}
                className="start-time-datepicker"
                placeholderText={fromText}
              />
              <span className="input-group-addon"><i className="fas fa-clock" /></span>
            </label>
          </div>
        </td>
        <td className="availability-entry">
          <div className="end-time-input-container g-row">
            <label className="g-row--always" htmlFor={`end-time-${index}-${proposalResourceId}`}>
              <DatePicker
                id={`end-time-${index}-${proposalResourceId}`}
                selected={availability.end_time}
                onChange={(newEndTime) => this.handleAvailabilityChange(newEndTime, index, 'end_time')}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={30}
                timeCaption="Time"
                dateFormat="h:mm aa"
                minTime={minEndTime(availability.start_time)}
                maxTime={maxEndTime()}
                className="end-time-datepicker"
                placeholderText={toText}
              />
              <span className="input-group-addon"><i className="fas fa-clock" /></span>
            </label>
          </div>
        </td>
      </Fragment>
    );
  }

  renderTableHeading() {
    const { timezone } = this.state;

    if (!timezone) {
      return null;
    }

    return (
      <div className="times-select-label body-text-2 medium-weight required mt-4">
        Add available times:
      </div>
    );
  }

  renderAvailabilitiesTable() {
    const { availabilities, timezone } = this.state;

    if (!timezone) {
      return null;
    }

    return (
      <table className="availabilities-table mt-2">
        <tbody>
          <tr>
            <th />
            <th>Day:</th>
            <th>Available from:</th>
            <th>To:</th>
          </tr>
          <tr className="with-bottom-border" />
          {
            availabilities.map((availability, index) => (
              <tr
                key={keyFrom(availability, index)}
                className={index % 2 ? 'gray-background' : ''}
                id={`slot${index}`}
              >
                {this.renderAvailabilityRow(availability, index)}
              </tr>
            ))
          }
          <tr>
            <td className="availability-entry add-slot" colSpan="4">
              <a
                className="body-text-2 medium-weight"
                role="button"
                tabIndex={0}
                onClick={() => this.handleAddAvailabilitySlot()}
              >
                <i className="fa fa-plus me-2" />
                Add Slot
                {' '}
                {availabilities.length + 1}
              </a>
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  renderNewAvailabilitiesTable() {
    const {
      timezone, currentViewIndex, selectedTimeSlots,
      isFetchingSlots, availableInterviewTimeSlots,
    } = this.state;

    if (!timezone) return null;

    let weekKey = null;
    let tableHeader = null;
    let timeSlots = [];

    const currentView = availableInterviewTimeSlots.length > 0
      ? availableInterviewTimeSlots[currentViewIndex]
      : [];

    if (currentView) {
      weekKey = Object.keys(currentView)[0] || [];
      if (weekKey.length) {
        [tableHeader, ...timeSlots] = currentView[weekKey] || [];
      }
    }

    return (
      <Fragment>
        {
          isFetchingSlots
            ? <Loading />
            : (
              <Fragment>
                <div className="date-navigation">
                  <button
                    type="button"
                    id="date-navigation-back"
                    className={`nav-button ${currentViewIndex === 0 ? 'disabled' : ''}`}
                    onClick={() => this.handleAvailabilityNavigation('back')}
                    disabled={currentViewIndex === 0}
                  >
                    <SVG src={arrowLeftIcon} className="arrow-icon" />
                  </button>
                  <div className="date-range">{weekKey}</div>
                  <button
                    type="button"
                    id="date-navigation-next"
                    className={`nav-button ${currentViewIndex === availableInterviewTimeSlots.length - 1 ? 'disabled' : ''}`}
                    onClick={() => this.handleAvailabilityNavigation('forward')}
                    disabled={currentViewIndex === availableInterviewTimeSlots.length - 1}
                  >
                    <SVG src={arrowRightIcon} className="arrow-icon" />
                  </button>
                  <button
                    className="today-button"
                    type="button"
                    onClick={() => this.setState({ currentViewIndex: 1 })}
                  >
                    Today
                  </button>
                </div>
                <div className="table-container">
                  <table className="scrollable-table">
                    <thead>
                      <tr>
                        {tableHeader && tableHeader.map((header, index) => (
                          <th key={`header-${index}`}>{header}</th>
                        ))}
                      </tr>
                    </thead>
                  </table>
                  <div className="scrollable-tbody">
                    <table className="scrollable-table">
                      <tbody>
                        {timeSlots && timeSlots.map((timeSlotRow, rowIndex) => (
                          <tr key={`slot-row-${rowIndex}`}>
                            {timeSlotRow.map((timeSlot, colIndex) => {
                              const isDisabled = ['passed', 'not_available', 'unavailable'].includes(timeSlot[2]);
                              const isSelected = hasMatchingTimeSlot(selectedTimeSlots, formatTimeSlot(timeSlot[0], timeSlot[1]));

                              return (
                                <td key={`slot-${rowIndex}-${colIndex}`}>
                                  <button
                                    id={`${timeSlot[0]}-${timeSlot[1].replace(' ', '-')}`}
                                    className={`btn-time-slot ${isDisabled ? 'btn-time-slot-disabled' : isSelected ? 'btn-time-slot-selected' : 'btn-time-slot-available'}`}
                                    disabled={isDisabled}
                                    onClick={() => this.handleSelectTimeSlot(formatTimeSlot(timeSlot[0], timeSlot[1]))}
                                  >
                                    {(isSelected && timeSlot[2] === 'available') && <SVG src={checkMarkIcon} className="check-mark" alt="checkmark icon" />}
                                    {timeSlot[2] === 'not_available' && <SVG src={phoneIcon} className="check-mark" alt="phone icon" />}
                                    <span>{timeSlot[1]}</span>
                                  </button>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <table className="scrollable-table">
                    <tfoot>
                      <tr>
                        <td>
                          <div className="btn-time-slot btn-time-slot-disabled">
                            <span>Unavailable Slots</span>
                          </div>
                        </td>
                        <td>
                          <div className="btn-time-slot btn-time-slot-available">
                            <span>Available Slots</span>
                          </div>
                        </td>
                        <td>
                          <div className="btn-time-slot btn-time-slot-selected">
                            <SVG src={checkMarkIcon} className="check-mark" alt="checkmark icon" />
                            <span>Selected Slots</span>
                          </div>
                        </td>
                        <td colSpan={2}>
                          <div className="btn-time-slot btn-time-slot-disabled">
                            <SVG src={phoneIcon} className="check-mark" alt="phone icon" />
                            <span>Upcoming Interivews</span>
                          </div>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </Fragment>
            )
        }
      </Fragment>
    );
  }

  renderInviteColleagues() {
    const {
      consultantName,
      invitableRoles,
      possibleInterviewers,
      excludedInterviewInvitees,
      isEditing,
      disableAvailabilitySelection,
    } = this.props;

    const { selectedCoworkers, newCoworkers, selectedInvitationRole } = this.state;

    if (!this.ableToSubmit()) {
      // don't show optional invite colleagues section
      // (unless able to submit)
      return null;
    }

    let excludedList;
    if (!isEditing && possibleInterviewers.current_user_permission) {
      excludedList = [possibleInterviewers.current_user_permission.email];
    } else {
      excludedList = excludedInterviewInvitees;
    }

    return (
      <div className="mb-50 invite-colleagues-container">
        { !disableAvailabilitySelection && <hr /> }
        <div className="invite-colleagues-header mb-4">
          <h2 className="normal-weight mb-2">
            <strong>
              Invite coworkers to participate in this interview
            </strong>
            &nbsp;&nbsp;(Optional)
          </h2>
          <div className="body-text-2">
            Coworkers will be notified with updates regarding the interview with
            {' '}
            {consultantName}
          </div>
        </div>
        <div className="invite-colleagues-container mb-3">
          <SelectOrInviteNewCoworkers
            invitableRoles={invitableRoles}
            allCoworkers={possibleInterviewers}
            excluded={excludedList}
            onChangeExistingCoworkers={this.handleSelectedCoworkersChange}
            selectedCoworkers={selectedCoworkers}
            onChangeNewCoworkers={this.handleNewCoworkersChange}
            newCoworkers={newCoworkers}
            onChangeNewCoworkersRole={this.handleSelectedInvitationRoleChange}
            selectedInvitationRole={selectedInvitationRole}
          />
        </div>
      </div>
    );
  }

  renderSubmitButton() {
    const { isEditing, interviewReschedule } = this.props;
    const disabled = !this.ableToSubmit();
    const tooltipCopy = `${NUM_SLOTS_REQUIRED} slots and a timezone are required in order to submit`;
    const buttonCopy = isEditing || interviewReschedule ? 'Save changes' : 'Submit availability';
    const onSubmit = isEditing || interviewReschedule
      ? () => !disabled && this.handleShowNotificationModal()
      : () => !disabled && this.handleSubmit();

    const submitButton = () => (
      <button
        className={`btn btn-primary submit-button ${disabled && 'disabled'}`}
        onClick={onSubmit}
        // can't use disabled prop here because a disabled button also disables the OverlayTrigger
        // instead, we prefer to check !disabled ourselves
      >
        {buttonCopy}
      </button>
    );

    const possiblyWrappedButton = () => (
      disabled
        ? (
          <OverlayTrigger
            placement="left"
            delay={200}
            overlay={(
              <Tooltip id="cannot-submit-tooltip">
                {tooltipCopy}
              </Tooltip>
          )}
          >
            {submitButton()}
          </OverlayTrigger>
        )
        : submitButton()
    );

    return (
      <div
        className="submit-button-container g-row--always g-row-justify-right g-flex-align-center"
      >
        {possiblyWrappedButton()}
      </div>
    );
  }

  renderModalTrigger() {
    const {
      modalTriggerCopy, modalTriggerClassName, modalTriggerType, introductionRequested,
    } = this.props;
    const className = modalTriggerClassName;
    const isButton = modalTriggerType === 'button';
    const triggerCopy = modalTriggerCopy;

    if (isButton) {
      const btnType = introductionRequested ? 'btn-secondary' : 'btn-primary';

      return (
        <button
          className={`btn ${btnType} ${className}`}
          onClick={() => this.setShowModal(true)}
        >
          {triggerCopy}
        </button>
      );
    }

    return (
      <a
        role="button"
        tabIndex={0}
        className={className}
        onClick={() => this.setShowModal(true)}
      >
        {triggerCopy}
      </a>
    );
  }

  renderModal() {
    const { disableAvailabilitySelection, newInterviewSlot } = this.props;

    return (
      <Modal
        show={this.state.showModal}
        onHide={() => this.setShowModal(false)}
        dialogClassName="get-interview-in-progress-modal"
        size="xl"
      >
        <Modal.Header closeButton />
        <div className="padded-modal-content style-guide">
          {!disableAvailabilitySelection && (
            <Fragment>
              {this.renderModalHeading()}
              {this.renderTimezoneSelect()}
              {!newInterviewSlot && this.renderTableHeading()}
              {newInterviewSlot
                ? this.renderNewAvailabilitiesTable()
                : this.renderAvailabilitiesTable()}
              {this.renderErrors()}
            </Fragment>
          )}
          {this.renderInviteColleagues()}
          {this.renderSubmitButton()}
        </div>
      </Modal>
    );
  }

  renderSendNotificationModal() {
    return (
      <Modal
        show={this.state.showSendNotificationModal}
        onHide={() => this.setState({ showModal: true, showSendNotificationModal: false })}
        dialogClassName="send-notification-modal"
        size="lg"
      >
        <Modal.Header closeButton />
        <div className="send-notification-modal-content style-guide">
          <h2 className="mb-4">
            Would you like to send notification emails to your coworkers participating in
            this meeting?
          </h2>
          <div
            className="send-notification-modal-actions"
          >
            <a
              role="button"
              tabIndex="0"
              onClick={() => this.setState({ showModal: true, showSendNotificationModal: false })}
              className="gray back-button body-text-2 medium-weight"
            >
              Back to editing
            </a>
            <a
              role="button"
              tabIndex="0"
              onClick={() => this.handleUpdateSubmission(false)}
              className="send-without-notifications body-text-2 medium-weight"
            >
              Save changes + don&apos;t send emails
            </a>
            <a
              role="button"
              tabIndex="0"
              onClick={() => this.handleUpdateSubmission(true)}
              className="send-with-notifications body-text-2 medium-weight"
            >
              Save changes + send emails
            </a>
          </div>
        </div>
      </Modal>
    );
  }

  render() {
    return (
      <div className="get-interview-in-progress-modal-container">
        {this.props.showModalTrigger && this.renderModalTrigger()}
        {this.renderModal()}
        {this.props.isEditing && this.renderSendNotificationModal()}
      </div>
    );
  }
}

ScheduleInterviewModal.propTypes = {
  createInterviewUrl: PropTypes.string.isRequired,
  proposalResourceId: PropTypes.string,
  csrfToken: PropTypes.string.isRequired,
  consultantName: PropTypes.string.isRequired,
  showNPSOnSubmit: PropTypes.bool,
  possibleInterviewers: PropTypes.shape({
    current_user_permission: PropTypes.shape({}),
    team_members: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  invitableRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  introductionRequested: PropTypes.bool,
  modalTriggerType: PropTypes.oneOf(['button', 'link']),
  modalTriggerCopy: PropTypes.string,
  modalTriggerClassName: PropTypes.string,
  availabilities: PropTypes.arrayOf(PropTypes.shape(
    { date: PropTypes.string, start_time: PropTypes.string, end_time: PropTypes.string },
  )),
  selectedCoworkers: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string, label: PropTypes.string,
  })),
  excludedInterviewInvitees: PropTypes.arrayOf(PropTypes.string), // can be emails or names
  isEditing: PropTypes.bool, // tells whether to PUT or POST to createInterviewUrl
  location: PropTypes.string,
  showModal: PropTypes.bool,
  disableAvailabilitySelection: PropTypes.bool,
  showModalTrigger: PropTypes.bool,
  interviewReschedule: PropTypes.bool,
  interviewType: PropTypes.string,
  newInterviewSlot: PropTypes.bool,
  handleInterviewModalClose: PropTypes.func,
  closeOverlay: PropTypes.func,
};

ScheduleInterviewModal.defaultProps = {
  introductionRequested: false,
  proposalResourceId: '',
  showNPSOnSubmit: false,
  modalTriggerType: 'button',
  modalTriggerCopy: 'Schedule Interview',
  modalTriggerClassName: 'schedule-interview-cta',
  availabilities: [],
  selectedCoworkers: [],
  isEditing: false,
  excludedInterviewInvitees: [],
  location: 'Location Not Specified',
  showModal: false,
  disableAvailabilitySelection: false,
  showModalTrigger: true,
  interviewReschedule: null,
  interviewType: null,
  newInterviewSlot: false,
  handleInterviewModalClose: () => {},
  closeOverlay: () => {},
};

export default ScheduleInterviewModal;
